import memberstackDOM from "@memberstack/dom";

const publicKey = process.env.VUE_APP_MEMBERSTACK_PUBLIC_KEY;
// The paid plan an expired-trial member checks out into. Its per-seat
// subscription prices are read at runtime via getPaidPlan().
const paidPlanId = process.env.VUE_APP_MEMBERSTACK_PLAN_ID;

// Optional, and normally unset. A separate Memberstack plan holding one small
// real price, used to prove the Memberstack -> Stripe -> bank path end to end
// without charging anyone a real tier ($2,160 and up). See TEST_TIER below.
const testPlanId = process.env.VUE_APP_MEMBERSTACK_TEST_PLAN_ID;

if (!publicKey) {
  console.warn(
    "[auth] VUE_APP_MEMBERSTACK_PUBLIC_KEY is not set — Memberstack will fail to init."
  );
}

export const memberstack = memberstackDOM.init({
  publicKey,
  useCookies: true,
  setCookieOnRootDomain: true,
});

const THIRTY_DAYS_MS = 30 * 24 * 60 * 60 * 1000;

// The division a member works in. Signup asked for it from 14 Aug 2026 until
// Anne had it removed on 1 Oct 2026 to keep the trial form as short as possible;
// AuthPanel no longer sends it, but members who signed up in between still have
// it, so getMemberDivision() and the GA user property keep reading it. Kept as
// a dropdown list (not free text) in case it comes back: "Registrar" /
// "registrar's office" / "Reg." would land in GA as three distinct values.
//
// Array order is the order rendered — "Other" stays last. The label is stored
// verbatim as the field value, so ADDING an option later is free but RENAMING or
// REMOVING one splits the reporting: members who already signed up keep the old
// string, in both Memberstack and GA's historical rows.
export const DIVISIONS = [
  "Registrar",
  "Admissions",
  "Curriculum Development",
  "Research",
  "Library",
  "Other",
];

function hasActivePlan(member) {
  const plans = member?.planConnections ?? [];
  return plans.some((p) => (p.status || "").toUpperCase() === "ACTIVE");
}

function dateInFuture(raw) {
  if (!raw) return false;
  const date = new Date(raw);
  return !Number.isNaN(date.getTime()) && date.getTime() > Date.now();
}

// `free-trial-date` may sit on the member (if configured as a custom field or
// metadata) or in the member's separate JSON store — which is where
// signupWithTrial writes it. Crucially, getCurrentMember() does NOT include the
// JSON store, so it has to be fetched on its own via getMemberJSON().
async function getTrialDate(member) {
  const onMember =
    member?.customFields?.["free-trial-date"] ??
    member?.metaData?.["free-trial-date"];
  if (onMember) return onMember;
  try {
    const { data } = await memberstack.getMemberJSON();
    return data?.["free-trial-date"] ?? data?.data?.["free-trial-date"] ?? null;
  } catch (err) {
    console.error("[auth] getMemberJSON failed:", err);
    return null;
  }
}

export async function hasActiveAccess() {
  try {
    const { data: member } = await memberstack.getCurrentMember();
    if (!member) return { authenticated: false, authorized: false };
    if (hasActivePlan(member)) {
      return { authenticated: true, authorized: true };
    }
    const trialDate = await getTrialDate(member);
    return { authenticated: true, authorized: dateInFuture(trialDate) };
  } catch (error) {
    console.error("[auth] hasActiveAccess failed:", error);
    return { authenticated: false, authorized: false };
  }
}

// No-card free trial: create the account and stamp a 30-day trial date. We do
// NOT attach a Memberstack plan — access during the trial is granted purely by
// the `free-trial-date` in member JSON (see hasActiveAccess). When the trial
// ends the member has no active plan, the gate closes, and they check out into
// the paid plan via startPlanCheckout(), which grants an ACTIVE plan.
export async function signupWithTrial({ email, password, division }) {
  await memberstack.signupMemberEmailPassword({
    email,
    password,
    // The `division` custom field must exist in the Memberstack app or the
    // value is silently discarded — it has to be created in the live app as
    // well as the sandbox.
    ...(division ? { customFields: { division } } : {}),
  });
  const expiry = new Date(Date.now() + THIRTY_DAYS_MS).toISOString();
  await memberstack.updateMemberJSON({
    json: { "free-trial-date": expiry },
  });
}

// The signed-in member's division, or null. Read through the auth module rather
// than calling Memberstack from App.vue, so this folder stays copy-pasteable
// into CoverageReport (see README).
export async function getMemberDivision() {
  try {
    const { data: member } = await memberstack.getCurrentMember();
    return member?.customFields?.division ?? null;
  } catch (err) {
    console.error("[auth] getMemberDivision failed:", err);
    return null;
  }
}

// Loads the paid plan (and its per-seat prices) for the expired-trial checkout
// picker. Returns the plan object: { id, name, description, prices: [...] }.
export async function getPaidPlan() {
  if (!paidPlanId) {
    throw new Error(
      "VUE_APP_MEMBERSTACK_PLAN_ID is not set — cannot load the paid plan for checkout."
    );
  }
  // Use getApp() rather than getPlan(): getPlan() omits each price's `interval`
  // (so we couldn't show the billing period). getApp() returns full price
  // objects — { amount, interval, currency, name, ... } — for every plan.
  const { data: app } = await memberstack.getApp();
  const plan = (app?.plans ?? []).find((p) => p.id === paidPlanId);
  if (!plan) {
    throw new Error(
      `Plan ${paidPlanId} is not connected to this Memberstack app.`
    );
  }
  return plan;
}

// The marketing pricing page links each tier card as ?plan=single|two|three.
// Price IDs differ between the sandbox and live Memberstack apps, so the slug is
// resolved against the plan's own price list at runtime instead of being baked
// into the Webflow links.
const TIER_MATCHERS = {
  single: /single/i,
  two: /two/i,
  three: /three/i,
};

// ?plan=test is a deliberate escape hatch for verifying the payment path with a
// small real charge. It exists ONLY while VUE_APP_MEMBERSTACK_TEST_PLAN_ID is
// set: unset that variable and the tier stops being recognised, ?plan=test
// falls through to the ordinary sign-up screen, and the affordance disappears
// with no code change. It is never surfaced in the in-app picker either, which
// only ever loads VUE_APP_MEMBERSTACK_PLAN_ID.
const TEST_TIER = "test";

export function isPlanTier(tier) {
  const slug = String(tier);
  if (slug === TEST_TIER) return !!testPlanId;
  return Object.prototype.hasOwnProperty.call(TIER_MATCHERS, slug);
}

// Seat order for every list of tiers we render: Single, Two, Three. Memberstack
// returns prices in its own order (roughly creation order), and sorting by
// amount only looks right while the per-seat totals happen to increase — so the
// order is derived from the tier name instead. A price whose name matches no
// tier sorts last, by amount, rather than disappearing.
const TIER_ORDER = ["single", "two", "three"];

export function tierRank(price) {
  const name = price?.name || "";
  const index = TIER_ORDER.findIndex((tier) => TIER_MATCHERS[tier].test(name));
  return index === -1 ? TIER_ORDER.length : index;
}

// The tier slug a price belongs to, or null when its name matches none. Callers
// use this to look up per-tier presentation (seat count, marketing copy) and
// must handle null by falling back to what the price object itself carries —
// same principle as tierRank: an unrecognised price degrades visibly instead of
// disappearing.
export function tierOf(price) {
  const name = price?.name || "";
  return TIER_ORDER.find((tier) => TIER_MATCHERS[tier].test(name)) ?? null;
}

// Seats per tier, used to turn Memberstack's single yearly figure into the
// per-user monthly headline the pricing page leads with.
export const TIER_SEATS = { single: 1, two: 2, three: 3 };

export function sortPricesByTier(prices) {
  return [...(prices ?? [])].sort(
    (a, b) => tierRank(a) - tierRank(b) || Number(a.amount) - Number(b.amount)
  );
}

// Resolves a tier slug to an ACTIVE yearly price on the paid plan, or null if
// nothing matches. Annual is the only billing period the pricing page
// advertises, so a tier whose active price is monthly is treated as a
// mis-configuration and refused — a wrong-interval price would charge twelve
// times the advertised amount. Callers fall back to the in-app picker, where
// the member sees the real amount and interval before committing. If monthly
// plans are ever sold from the marketing site, relax this filter and update the
// page copy in the same change.
// The test plan is a different plan from the advertised one, and its price is
// deliberately NOT bound by the yearly rule below: nothing advertises it, so
// there is no annual figure a monthly or one-time price could contradict.
// Returns null when the plan has no active price, which is how it ships.
async function getTestPrice() {
  if (!testPlanId) return null;
  const { data: app } = await memberstack.getApp();
  const plan = (app?.plans ?? []).find((p) => p.id === testPlanId);
  const price = (plan?.prices ?? []).find(
    (p) => (p.status || "").toUpperCase() === "ACTIVE"
  );
  if (!price) {
    console.error(
      `[pricing] test plan ${testPlanId} has no active price — attach one in Memberstack.`
    );
    return null;
  }
  return price;
}

export async function getPriceForTier(tier) {
  if (String(tier) === TEST_TIER) return getTestPrice();
  const matcher = TIER_MATCHERS[String(tier)];
  if (!matcher) return null;
  const plan = await getPaidPlan();
  const matching = (plan?.prices ?? []).filter(
    (p) =>
      (p.status || "").toUpperCase() === "ACTIVE" && matcher.test(p.name || "")
  );
  const yearly = matching.filter(
    (p) => (p.interval?.type || "").toUpperCase() === "YEARLY"
  );
  if (!yearly.length) {
    if (matching.length) {
      console.error(
        `[pricing] tier "${tier}" has no active yearly price (found: ${matching
          .map((p) => `${p.name} ${p.interval?.type}`)
          .join(", ")}) — refusing to check out.`
      );
    }
    return null;
  }
  if (yearly.length > 1) {
    console.warn(
      `[pricing] tier "${tier}" matches ${yearly.length} active yearly prices — using the first.`
    );
  }
  return yearly[0];
}

// Checkout entry point for the ?plan= deep link. Resolves the tier, then hands
// off to Stripe. Returns false when no active price matches, so the caller can
// fall back to the in-app picker rather than dead-ending the buyer.
export async function startTierCheckout(tier) {
  const price = await getPriceForTier(tier);
  if (!price) return false;
  await startPlanCheckout({ priceId: price.id });
  return true;
}

// Sends the (authenticated) member to Stripe Checkout for the chosen price.
// Memberstack auto-redirects; on success Stripe returns to successUrl, where
// refreshAccess() sees the now-ACTIVE plan and opens the gate. On cancel it
// returns to the current page (the expired-trial screen).
export async function startPlanCheckout({ priceId }) {
  return memberstack.purchasePlansWithCheckout({
    priceId,
    successUrl: window.location.origin,
    cancelUrl: window.location.href,
  });
}

// Aggregated view of the member's account for the Account panel. Returns raw
// values (the UI formats them): plan/trial status, price, next billing date,
// and owned team. status is "paid" | "trial" | "none".
export async function getAccountSummary() {
  const { data: member } = await memberstack.getCurrentMember();
  if (!member) return null;

  const activeConn = (member.planConnections ?? []).find(
    (c) => (c.status || "").toUpperCase() === "ACTIVE"
  );

  let plan = null;
  let nextBillingDate = null;
  if (activeConn) {
    nextBillingDate = activeConn.payment?.nextBillingDate ?? null;
    try {
      const { data: app } = await memberstack.getApp();
      const p = (app?.plans ?? []).find((x) => x.id === activeConn.planId);
      const price = (p?.prices ?? []).find(
        (pr) => pr.id === activeConn.payment?.priceId
      );
      plan = {
        planName: p?.name ?? "",
        tierName: price?.name ?? "",
        amount: price?.amount ?? activeConn.payment?.amount ?? null,
        currency: price?.currency ?? activeConn.payment?.currency ?? "usd",
        interval: price?.interval?.type ?? null,
      };
    } catch (err) {
      console.error("[account] plan lookup failed:", err);
    }
  }

  const trialEndsAt = activeConn ? null : await getTrialDate(member);
  const status = activeConn
    ? "paid"
    : dateInFuture(trialEndsAt)
    ? "trial"
    : "none";

  return {
    email: member.auth?.email ?? "",
    stripeCustomerId: member.stripeCustomerId ?? null,
    ownedTeamId: member.teams?.ownedTeams?.[0]?.teamId ?? null,
    status,
    plan,
    nextBillingDate,
    trialEndsAt,
  };
}

// Opens Stripe's hosted billing portal (update card, invoices, cancel).
// Auto-redirects the browser; returns to the current page afterward.
export async function launchBillingPortal() {
  return memberstack.launchStripeCustomerPortal({
    returnUrl: window.location.href,
  });
}

// ---- Team accounts (multi-seat plans) ----
// The DOM SDK types these as Promise<void>, but at runtime they resolve with a
// { data } payload (verified against the live sandbox), so we read it.

// The teamId the current member OWNS, or null if they don't own a team.
export async function getOwnedTeamId() {
  try {
    const { data: member } = await memberstack.getCurrentMember();
    return member?.teams?.ownedTeams?.[0]?.teamId ?? null;
  } catch (err) {
    console.error("[team] getOwnedTeamId failed:", err);
    return null;
  }
}

// Full team detail for the manage dialog:
// { id, inviteToken, maxTeamMembers, members: [{ role, member: { id, auth: { email } } }] }
export async function getTeamDetail(teamId) {
  const { data } = await memberstack.getTeam({ teamId });
  return data;
}

// Rotates the team's invite link; returns the fresh token.
export async function regenerateInviteToken(teamId) {
  const { data } = await memberstack.generateInviteToken({ teamId });
  return data?.inviteToken ?? null;
}

export async function removeTeamMember({ teamId, memberId }) {
  return memberstack.removeMemberFromTeam({ teamId, memberId });
}

// Invitee — brand-new account that joins a team via an invite token. Unlike
// signupWithTrial, it attaches no trial and no plan: access comes from the
// team's (owner-paid) plan connection.
export async function signupTeamMember({
  email,
  password,
  inviteToken,
  division,
}) {
  await memberstack.signupMemberEmailPassword({
    email,
    password,
    inviteToken,
    // Invitees are asked their division too — they're users like any other, and
    // leaving seats off would put a hole in exactly the segmentation this is for.
    ...(division ? { customFields: { division } } : {}),
  });
}

// Invitee — existing member joins a team via an invite token.
export async function joinTeamWithToken(inviteToken) {
  return memberstack.joinTeam({ inviteToken });
}

export async function login({ email, password }) {
  await memberstack.loginMemberEmailPassword({ email, password });
}

export async function sendResetEmail({ email }) {
  await memberstack.sendMemberResetPasswordEmail({ email });
}

export async function logout() {
  await memberstack.logout();
}
